import { useState } from 'react';
import { X } from 'lucide-react';
import { Button, Alert } from '@dreamtree-org/twreact-ui';
import InputSchemaForm, { missingRequired, valuesToInput } from '@/components/workflow/InputSchemaForm';

/**
 * "Test" — collects the run input before triggering a run.
 *
 * Previously the Run button always posted `{}`, so a workflow with an authored input
 * contract could not be exercised from the UI at all: every `{{input.x}}` resolved empty and
 * the graph either failed or produced nonsense.
 *
 * The form itself lives in InputSchemaForm.jsx, shared with the API tab's Interactive
 * Console — the two screens ask for the same thing and must not drift on how a field renders
 * or how a value is coerced.
 *
 * With no schema there is nothing to ask for, so it just confirms and runs.
 */

const overlayClass = 'fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4';
const panelClass = 'bg-[var(--app-surface)] rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col';

export default function TestDialog({ fields = [], onClose, onRun }) {
  const [values, setValues] = useState({});
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');

  const missing = missingRequired(fields, values);

  const run = async () => {
    setRunning(true);
    setErr('');
    try {
      await onRun(valuesToInput(fields, values));
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
      setRunning(false);
    }
  };

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
          <span className="font-semibold text-sm">Test run</span>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[var(--app-border)]/40">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3 overflow-y-auto">
          {err && <Alert variant="error">{err}</Alert>}
          <InputSchemaForm
            fields={fields}
            values={values}
            onChange={(name, v) => setValues((s) => ({ ...s, [name]: v }))}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--app-border)]">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={run} disabled={running || missing.length > 0}>
            {running ? 'Starting…' : 'Run'}
          </Button>
        </div>
      </div>
    </div>
  );
}
